import { WikiGenerationStatus } from '@/generated/prisma/enums';

/**
 * The newest generation attempt when it is newer than the wiki being served and has not
 * completed. `/api/wikis/latest` serves the newest completed version and reports this beside it.
 */
export type WikiRefreshAttempt = {
  id: string;
  version: number;
  generation_status: WikiGenerationStatus;
  error_message: string | null;
  created_at: string;
  updated_at: string;
};

export type WikiRefreshStatus = 'in_progress' | 'failed' | null;

/** Why a generation failed, as far as the reader can act on it. */
export type WikiFailureType = 'timeout' | 'permission' | 'unknown';

// Written when an attempt never reports back (failAbandonedWikiGenerations).
const ABANDONED_GENERATION_MESSAGE = 'Wiki generation timed out. Please start generation again.';
// The generator's own time limit, e.g. "Generation timed out after 6 hours".
const GENERATOR_TIMEOUT_PREFIX = 'Generation timed out after ';
// The generator could not get a repository token from cubic for the attempt.
const MISSING_REPOSITORY_CREDENTIAL_MESSAGE = 'Wiki repository credential was not found';
// git clone refused by GitHub: no access to the repository, or it no longer exists.
const GITHUB_CLONE_ACCESS_ERROR =
  /Error during cloning:[\s\S]*(?:requested URL returned error: 40[34]|Repository not found)/i;

/**
 * Classifies a failed generation's error message. Only failures the reader can act on get a
 * specific explanation; anything else, including messages that merely mention a status code
 * or a permission, gets the generic one.
 */
export function getWikiFailureType(errorMessage: string | null): WikiFailureType {
  if (!errorMessage) {
    return 'unknown';
  }

  if (
    errorMessage === ABANDONED_GENERATION_MESSAGE ||
    errorMessage.startsWith(GENERATOR_TIMEOUT_PREFIX)
  ) {
    return 'timeout';
  }

  if (
    errorMessage.includes(MISSING_REPOSITORY_CREDENTIAL_MESSAGE) ||
    GITHUB_CLONE_ACCESS_ERROR.test(errorMessage)
  ) {
    return 'permission';
  }

  return 'unknown';
}

/**
 * Status of a refresh behind the served wiki: running, failed, or none to report.
 */
export function getWikiRefreshStatus(wikiData: any): WikiRefreshStatus {
  const attempt: WikiRefreshAttempt | null | undefined = wikiData?.refresh_attempt;
  switch (attempt?.generation_status) {
    case WikiGenerationStatus.pending:
    case WikiGenerationStatus.generating:
    case WikiGenerationStatus.processing:
      return 'in_progress';
    case WikiGenerationStatus.failed:
    case WikiGenerationStatus.error:
      return 'failed';
    default:
      return null;
  }
}

/**
 * The version the unversioned wiki URL serves, from a version list ordered newest first: the
 * newest completed one, else the newest attempt. A refresh running or failed is listed above
 * it without replacing it.
 */
export function getDefaultWikiVersionNumber(
  wikiVersions: Array<{ version: number; generation_status?: string }> | undefined,
): number {
  const defaultVersion =
    wikiVersions?.find((version) => version.generation_status === WikiGenerationStatus.completed) ??
    wikiVersions?.[0];
  return defaultVersion?.version ?? 1;
}

export function getWikiStatusFlags(wikiData: any) {
  const isWikiPending = wikiData?.generation_status === WikiGenerationStatus.pending;
  const isWikiGenerating =
    wikiData?.generation_status === WikiGenerationStatus.generating ||
    wikiData?.generation_status === WikiGenerationStatus.processing;
  const isWikiCompleted = wikiData?.generation_status === WikiGenerationStatus.completed;
  const isWikiFailed =
    wikiData?.generation_status === WikiGenerationStatus.failed ||
    wikiData?.generation_status === WikiGenerationStatus.error;

  const isWikiInProgress = isWikiPending || isWikiGenerating;

  const hasValidWikiContent =
    wikiData &&
    isWikiCompleted &&
    ((wikiData.pages && wikiData.pages.length > 0) ||
      (wikiData.cache_data?.generated_pages &&
        Object.keys(wikiData.cache_data.generated_pages).length > 0));

  return {
    isWikiPending,
    isWikiGenerating,
    isWikiCompleted,
    isWikiFailed,
    isWikiInProgress,
    hasValidWikiContent,
  };
}

export function parseWikiData(wikiData: any) {
  let pages: any[] = [];
  let wikiStructure: any = null;

  if (!wikiData) {
    return { pages, wikiStructure };
  }

  if (wikiData.pages && wikiData.pages.length > 0) {
    // Old format
    pages = wikiData.pages;
  } else if (wikiData.cache_data) {
    // New format with cache_data
    wikiStructure = wikiData.cache_data.wiki_structure;
    const processedGeneratedPages = wikiData.cache_data.generated_pages || {};

    // Convert generated_pages to pages array
    pages = Object.values(processedGeneratedPages).map((page: any) => ({
      pageRef: page.id,
      title: page.title,
      contentMd: page.content,
      content: page.content,
      importance: page.importance || 'medium',
      filePaths: page.filePaths || [],
      relatedPages: page.relatedPages || [],
    }));
  }

  return { pages, wikiStructure };
}
